import { IS_DEV_BUILD, DEV_FRONTDOOR_BASE_URL, DEV_SSO_LOGIN_URL, DOGFOOD_SSO_LOGIN_URL, DOGFOOD_SSO_LOGIN_URL_1CDN, MSIT_SSO_LOGIN_URL, MSIT_SSO_LOGIN_URL_1CDN, PROD_SSO_LOGIN_URL, PROD_SSO_LOGIN_URL_1CDN, AG08_SSO_LOGIN_URL, AG09_SSO_LOGIN_URL } from "../../AppConstants";
import { getAuthEnvironmentFromAugLoopServiceUrl } from "../../utils/AuthEnvironmentUtil";
import { AuthEnvironment } from "../interfaces/IAuthenticationProvider";
import { TranscriptionStorageType } from "../interfaces/IStoredTranscription";
import { enable1CdnLoginUrl } from '../../utils/ChangeGate';

export enum HostAppName {
  WORD = 'WORD',    // Word App
  ONENOTE = 'ONENOTE'    // OneNote App
}

export enum HostAppSettingName {
    AugLoopCloudALServiceUrl = 'AugLoopCloudALServiceUrl',
    IsProductionEnvironment = 'IsProductionEnvironment',
    TranscriptionSessionId = 'TranscriptionSessionId',
    MonthlyMinutesUsed = 'MonthlyMinutesUsed',
    HasStoredTranscription = 'HasStoredTranscription',
    StoredTranscription = 'StoredTranscription',
    IsFreemiumUpsellEnabled = 'IsFreemiumUpsellEnabled',
    IsLicensedUser = 'IsLicensedUser',
    IsCopilotLicensedUser = 'IsCopilotLicensedUser',
    DefaultTranscriptionLocale = 'DefaultTranscriptionLocale',
    FrontDoorBaseUrl = 'FrontDoorBaseUrl',
    UnlimitedMins = 'UnlimitedMins',
    UserTenantId = 'UserTenantId',
    IsSharedUXDarkModeOn = 'isSharedUXDarkModeOn',
    HostAppName = 'HostAppName',
    IsRecordingEventCaptureEnabled = 'IsRecordingEventCaptureEnabled',
    EnableFlightAuthVersion = 'EnableFlightAuthVersion',
    AssociatedOneNotePageId = 'AssociatedOneNotePageId',
    AssociatedOneNoteNotebookId = 'AssociatedOneNoteNotebookId',
    AssociatedOneNoteSectionId = 'AssociatedOneNoteSectionId',
    IsTranscriptionNewFlowEnabled = 'IsTranscriptionNewFlowEnabled',
    NotifyTranscriptionEventToHostAppEnabled = 'NotifyTranscriptionEventToHostAppEnabled',
    IsMultiTranscriptionStorageSupported = 'IsMultiTranscriptionStorageSupported',
    MultiTranscriptStorageLimitPerPage = 'MultiTranscriptStorageLimitPerPage',
    ShouldShowOneDriveIdentityInfoDialog = 'ShouldShowOneDriveIdentityInfoDialog',
    IsAssociatedPageDeleted = 'IsAssociatedPageDeleted',
    AddTranscriptionIndicatorToHostEnabled = 'AddTranscriptionIndicatorToHostEnabled',
    OneNoteNotebookIdentityEmail = 'OneNoteNotebookIdentityEmail',
    AssociatedOneNotePageHyperLink = 'AssociatedOneNotePageHyperLink',
    IsTranscriptionFetchedFromCustomProperties = 'IsTranscriptionFetchedFromCustomProperties',
    WordDocumentIdentityEmail = 'WordDocumentIdentityEmail',
    SDXPackageVersion = 'SDXPackageVersion',
    Audience = 'Audience',
    DocumentStorageType = 'DocumentStorageType'
};

/**
 * Is a wrapper around the settings that are passed in to the SDX from the hosting app
 */
export class HostAppSettings {
    /**
     * Returns a string parsed from reading the setting
     * @param settingName  Name of setting to read
     */
    public static GetSettingAsNumber(settingName: HostAppSettingName): number {
      let settingVal = Office.context.document.settings.get(settingName.toString());
      if (settingVal === null) {
        return 0;
      }
      return ((typeof settingVal === "number" || settingVal === undefined) ? settingVal : parseInt(settingVal.toString()));
    }

    /**
     * Returns a string parsed from reading the setting
     * @param settingName  Name of setting to read
     */
    public static GetSettingAsString(settingName: HostAppSettingName): string {
      let settingVal = Office.context.document.settings.get(settingName.toString());
      return ((settingVal === null || settingVal === undefined) ? settingVal : settingVal.toString());
    }

    /**
     * Returns an object associated with passed setting
     * @param settingName  Name of setting to read
     */
    public static GetSetting(settingName: HostAppSettingName): any {
      return Office.context.document.settings.get(settingName.toString());
    }

    /**
     * Sets a setting in the in-memory settings bag
     * @param settingName  Name of setting to set
     * @param value Value to set to
     */
    public static Set(settingName: HostAppSettingName, value: any): void {
        Office.context.document.settings.set(settingName,
            typeof value == "string"? value: JSON.stringify(value));
    }

    /**
     * Returns false if setting doesn't exist
     * @param settingName Name of setting to read
     */
    public static IsSettingTrue(settingName: HostAppSettingName): boolean {
        const settingValue: string = this.GetSettingAsString(settingName);
        if (settingValue) {
            return (settingValue.toLowerCase() == 'true');
        } else {
            return false;
        }
    }
};

export function GetInitialMonthlyMinutesUsed(): number {
    if (IS_DEV_BUILD)
        return 42;

    return HostAppSettings.GetSettingAsNumber(HostAppSettingName.MonthlyMinutesUsed);
}

export function getAuthEnvironment(): AuthEnvironment {
    const augloopServiceUrl = HostAppSettings.GetSettingAsString(HostAppSettingName.AugLoopCloudALServiceUrl);
    return getAuthEnvironmentFromAugLoopServiceUrl(augloopServiceUrl);
}

/**
 * Returns the SSO login URL for old CDN
 */
function GetSSOLoginUrlForOldCdn(): string {
  if (IS_DEV_BUILD)
    return DEV_SSO_LOGIN_URL;

  const augloopServiceUrl = HostAppSettings.GetSettingAsString(HostAppSettingName.AugLoopCloudALServiceUrl);
  const authEnv = getAuthEnvironmentFromAugLoopServiceUrl(augloopServiceUrl);
  const audience = HostAppSettings.GetSettingAsString(HostAppSettingName.Audience);

  if (authEnv === AuthEnvironment.USNAT) {
    return AG08_SSO_LOGIN_URL;
  }
  if (authEnv === AuthEnvironment.USSEC) {
    return AG09_SSO_LOGIN_URL;
  }
  if (authEnv === AuthEnvironment.MSIT) {
    return MSIT_SSO_LOGIN_URL;
  }

  if (audience === "DOGFOOD") {
    return DOGFOOD_SSO_LOGIN_URL;
  }

  return PROD_SSO_LOGIN_URL;
}

/**
 * Returns the SSO login URL for 1CDN
 */
function GetSSOLoginUrlFor1Cdn(): string {
  if (IS_DEV_BUILD)
    return DEV_SSO_LOGIN_URL;

  const augloopServiceUrl = HostAppSettings.GetSettingAsString(HostAppSettingName.AugLoopCloudALServiceUrl);
  const authEnv = getAuthEnvironmentFromAugLoopServiceUrl(augloopServiceUrl);
  const audience = HostAppSettings.GetSettingAsString(HostAppSettingName.Audience);

  if (authEnv === AuthEnvironment.USNAT) {
    return AG08_SSO_LOGIN_URL;
  }
  if (authEnv === AuthEnvironment.USSEC) {
    return AG09_SSO_LOGIN_URL;
  }
  if (authEnv === AuthEnvironment.MSIT) {
    return MSIT_SSO_LOGIN_URL_1CDN;
  }

  if (audience === "DOGFOOD") {
    return DOGFOOD_SSO_LOGIN_URL_1CDN;
  }

  return PROD_SSO_LOGIN_URL_1CDN;
}

export function GetSSOLoginUrl(): string {
  return enable1CdnLoginUrl ? GetSSOLoginUrlFor1Cdn() : GetSSOLoginUrlForOldCdn();
}

export function GetFrontDoorBaseUrl(): string {
    if (IS_DEV_BUILD)
      return DEV_FRONTDOOR_BASE_URL;

    let frontdoorURL = HostAppSettings.GetSettingAsString(HostAppSettingName.FrontDoorBaseUrl);

    if (frontdoorURL.startsWith("wss:")) {
      frontdoorURL = frontdoorURL.replace("wss:", "https:");
    }

    if (!frontdoorURL.startsWith("https:")) {
      frontdoorURL = 'https://' + frontdoorURL;
    }

    if (!frontdoorURL.endsWith("/SpeechHandler.ashx")) {
      frontdoorURL = frontdoorURL + '/SpeechHandler.ashx';
    }

    return frontdoorURL;
}

export function GetSupportedTranscriptionStorageType(): TranscriptionStorageType {
  const storageType = HostAppSettings.GetSettingAsString(HostAppSettingName.DocumentStorageType);
  switch (storageType) {
    case TranscriptionStorageType.DocumentXmlStorage:
      return TranscriptionStorageType.DocumentXmlStorage;
    case TranscriptionStorageType.DocumentStorage:
      return TranscriptionStorageType.DocumentStorage;
    case TranscriptionStorageType.UnKnownStorage:
      return TranscriptionStorageType.UnKnownStorage;
    default:
      return TranscriptionStorageType.DocumentStorage;
  }
}
